import './code-highlight-block.css';

import type { Options } from 'react-markdown';

import remarkGfm from 'remark-gfm';
import { memo, useMemo } from 'react';
import ReactMarkdown from 'react-markdown';
import rehypeHighlight from 'rehype-highlight';
import { varAlpha } from 'minimal-shared/utils';

import Link from '@mui/material/Link';
import { styled } from '@mui/material/styles';

import { themeConfig } from '../../theme/theme-config';
import { rehypeChatText, CHAT_MARKDOWN_QUOTE_CLASS } from './rehype-chat-text';

// ----------------------------------------------------------------------

const UNSPOKEN_OPACITY = 0.5;
const CODE_FONT_FAMILY = "'JetBrainsMono', monospace";

const remarkPlugins: NonNullable<Options['remarkPlugins']> = [[remarkGfm, { singleTilde: false }]];

// No rehype-raw: assistant text can carry connector content, so raw HTML stays text.
const components: NonNullable<Options['components']> = {
  a: ({ href, children }) =>
    href ? (
      <Link href={href} target="_blank" rel="noopener noreferrer" underline="always" color="inherit">
        {children}
      </Link>
    ) : (
      children
    ),
  img: () => null,
};

const ChatMarkdownRoot = styled('div')(({ theme }) => ({
  '& > :last-child': { marginBottom: 0 },
  '& p': { margin: 0, marginBottom: theme.spacing(1.5) },
  '& h1, & h2, & h3, & h4, & h5, & h6': {
    font: 'inherit',
    fontWeight: theme.typography.fontWeightSemiBold,
    margin: theme.spacing(2, 0, 0.5),
    '&:first-of-type': { marginTop: 0 },
  },
  '& ul, & ol': { margin: theme.spacing(0, 0, 1.5), paddingLeft: theme.spacing(3) },
  '& li': { margin: theme.spacing(0.5, 0) },
  '& li > p': { margin: 0 },
  '& blockquote': {
    margin: theme.spacing(0, 0, 1.5),
    padding: theme.spacing(0, 0, 0, 2),
    borderLeft: `2px solid ${theme.vars.palette.divider}`,
    color: theme.vars.palette.text.secondary,
  },
  '& code': {
    fontFamily: CODE_FONT_FAMILY,
    fontWeight: theme.typography.fontWeightRegular,
    fontSize: '0.9em',
  },
  '& :not(pre) > code': {
    padding: theme.spacing(0.25, 0.75),
    borderRadius: Number(theme.shape.borderRadius) / 2,
    backgroundColor: varAlpha(theme.vars.palette.grey['600Channel'], 0.2),
    wordBreak: 'break-word',
  },
  '& pre': {
    margin: theme.spacing(0, 0, 1.5),
    padding: theme.spacing(1.5, 2),
    borderRadius: theme.shape.borderRadius,
    backgroundColor: theme.vars.palette.container.low,
    lineHeight: 1.5,
    whiteSpace: 'pre-wrap',
    wordBreak: 'break-word',
    overflowX: 'auto',
  },
  '& table': {
    margin: theme.spacing(0, 0, 1.5),
    borderCollapse: 'collapse',
    border: `1px solid ${theme.vars.palette.divider}`,
    '& th, & td': {
      padding: theme.spacing(0.75, 1.5),
      border: `1px solid ${theme.vars.palette.divider}`,
      textAlign: 'left',
    },
    '& th': { fontWeight: theme.typography.fontWeightSemiBold },
  },
  '& hr': {
    margin: theme.spacing(1.5, 0),
    border: 0,
    borderTop: `1px solid ${theme.vars.palette.divider}`,
  },
  '& [data-spoken="false"]': { opacity: UNSPOKEN_OPACITY },
  [`& .${CHAT_MARKDOWN_QUOTE_CLASS}`]: {
    background: themeConfig.gradients.primary.background,
    WebkitBackgroundClip: 'text',
    backgroundClip: 'text',
    color: 'transparent',
  },
}));

// ----------------------------------------------------------------------

export interface ChatMarkdownProps {
  children: string;
  /** Source characters before this offset render spoken; the rest render dimmed. */
  spokenChars?: number;
  /** Wrap the message in gradient curly quotes. */
  quoted?: boolean;
}

/** Shared assistant-message renderer for the live Ari view and chat history. */
export const ChatMarkdown = memo(function ChatMarkdown({
  children,
  spokenChars,
  quoted,
}: ChatMarkdownProps): React.JSX.Element {
  const rehypePlugins = useMemo<NonNullable<Options['rehypePlugins']>>(
    () => [
      [rehypeHighlight, { detect: false }],
      [rehypeChatText, { spokenChars, quoted }],
    ],
    [spokenChars, quoted]
  );

  return (
    <ChatMarkdownRoot data-testid="ari.chat-markdown">
      <ReactMarkdown remarkPlugins={remarkPlugins} rehypePlugins={rehypePlugins} components={components}>
        {children}
      </ReactMarkdown>
    </ChatMarkdownRoot>
  );
});
